/**
 * ``ObscurationLayer`` — non-visual React component that renders every
 * platform's **obscuration** rings as GeoJSON fills + outlines (PR5b inc4;
 * REQ-UI-070; ADR-032 §4.2). The obscuration sibling of :func:`FovLayer` /
 * :func:`CoverageLayer`; all three share the geometry store, the toggle
 * hook, and the gated-effect lifecycle.
 *
 * Obscuration is a **list** of rings per platform, so a single platform may
 * contribute several polygon features (one per ring) — see
 * :func:`obscurationToFeatureCollection`. Otherwise the behaviour mirrors
 * :func:`FovLayer`:
 *   1. read the per-platform geometry from the shared geometry store
 *      (:func:`usePlatformGeometry`), READ-ONLY (the store's SSE bridge is
 *      owned by :file:`use-map-sse-bridges.ts`);
 *   2. gate rendering behind the operator's ``obscurationVisible`` toggle AND
 *      ``zoom >= GEOMETRY_MIN_ZOOM`` (the shared perf gate);
 *   3. attach / update in place when the gate is open; detach when it closes.
 *
 * (2)+(3) are delegated to the shared :func:`useGeometryLayerEffect`
 * (CLAUDE.md §4.4 — no duplicated gate/listener logic across the three
 * geometry layers). This component binds only the obscuration-specific
 * pieces: the ``obscurationVisible`` flag and the obscuration attach/detach.
 *
 * Cross-references:
 *   - REQ-UI-070 (off-by-default, zoom-gated geometry) / REQ-UI-002 (WebGL).
 *   - ADR-032 §4.2 (Status geometry data model — obscuration ring list).
 *   - CLAUDE.md §5.8 (perf discipline) / §8.1 (operator visibility) /
 *     §4.4 (no duplicated logic).
 *   - map/geometry-layer-effect.ts (the shared gated lifecycle).
 *   - map/geometry-store.ts · map/use-geometry-layers.ts ·
 *     map/obscuration-layer-impl.ts.
 */
import type { Map as MapLibreMap } from "maplibre-gl";
import { usePlatformGeometry } from "@/map/geometry-store";
import { useGeometryVisibility } from "@/map/use-geometry-layers";
import { useGeometryLayerEffect } from "@/map/geometry-layer-effect";
import { attachObscurationLayer, detachObscurationLayer } from "@/map/obscuration-layer-impl";

/** Props for :func:`ObscurationLayer`. */
export interface ObscurationLayerProps {
  /** Live MapLibre map instance (mounted after the map's ``load``). */
  map: MapLibreMap;
}

/**
 * Render + gate the obscuration polygon layer. Returns no DOM (WebGL-only).
 */
export function ObscurationLayer({ map }: ObscurationLayerProps): null {
  // Read-only: the geometry store is fed by the status-geometry SSE bridge
  // in useMapSseBridges (see module docstring). We subscribe + paint.
  const entries = usePlatformGeometry();
  // Visibility is the active Profile's map.visible_layers.obscuration,
  // falling back to false off-by-default (REQ-UI-089 / REQ-UI-070). Single
  // source — the per-session override layer was removed with the map cog
  // menu (item 3a).
  const config = useGeometryVisibility();

  // The shared gated lifecycle: subscribe to zoom, attach when
  // ``obscurationVisible && zoom >= GEOMETRY_MIN_ZOOM`` else detach.
  useGeometryLayerEffect({
    map,
    entries,
    visible: config.obscurationVisible,
    attach: attachObscurationLayer,
    detach: detachObscurationLayer,
  });

  return null;
}
